// watch/ReactionPair.tsx — Cherish | Dislike as one control. Likes were merged into Cherish,
// so the appreciation half is the platform's one reaction here too. (PREVIEW: this reopens the
// 2026-09-26 "Like / Dislike, no Cherish" ruling and needs the owner's word.) Both counts
// equally visible, no text labels. The two are one choice: picking one clears the other
// (server: PostViewSet.cheer / .dislike).
import { useState } from 'react';
import { Icon } from '@/icons';
import { useRequireAuth } from '@/hooks';
import { useToast } from '@/ui';
import { formatCount } from '@/components/formatCount';
import { postsService } from '@/core/services/postsService';
import type { Post } from '@/core/models/post';
import { SegmentBtn, Segmented } from './actionStyles';

export function ReactionPair({ post }: { post: Post }) {
  const requireAuth = useRequireAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const liked = post.cheered;
  const disliked = Boolean(post.disliked);

  async function run(kind: 'like' | 'dislike') {
    if (!requireAuth(kind === 'like' ? 'cherish' : 'dislike')) return;
    setBusy(true);
    try {
      const next =
        kind === 'like'
          ? await postsService.toggleCheer(post.id)
          : await postsService.toggleDislike(post.id);
      if (!next) toast('Something went wrong');
    } catch {
      toast('Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Segmented role="group" aria-label="Reactions">
      <SegmentBtn
        type="button"
        $on={liked}
        aria-pressed={liked}
        aria-label={`Cherish, ${post.cheers} ${post.cheers === 1 ? 'cherish' : 'cherishes'}`}
        disabled={busy}
        onClick={() => void run('like')}
      >
        <Icon name="cherish" size={18} />
        {formatCount(post.cheers)}
      </SegmentBtn>
      <SegmentBtn
        type="button"
        $on={disliked}
        aria-pressed={disliked}
        aria-label={`Dislike, ${post.dislikes ?? 0} ${post.dislikes === 1 ? 'dislike' : 'dislikes'}`}
        disabled={busy}
        onClick={() => void run('dislike')}
      >
        <Icon name="thumbs-down" size={18} />
        {formatCount(post.dislikes ?? 0)}
      </SegmentBtn>
    </Segmented>
  );
}
